import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { CurrentUserService } from 'src/app/user/services';
import { InitStep } from '../../distribution/models/init-step';
import { InitializeService } from '../../distribution/services/initialize.service';
import { GenericModalService, ModalMultiButton, ModalSettingsConfirmMultiButton, ModalSettingsMessage } from '../../shared/generic-modal/generic-modal.service';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { Artifact, ArtifactAssemblyTypes, AssemblyArtifact } from '../models/artifact';
import { DeveloperArtifactService } from '../services/developer-artifact.service';
import { DeveloperService } from '../services/developer.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DeveloperArtifactAssemblyPickerComponent } from '../developer-artifacts/developer-artifact-assembly-picker.component';

@Component({
    selector: 'app-developer-init-steps',
    templateUrl: './developer-init-steps.component.html',
    styleUrls: ['./developer-init-steps.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule, DxTemplateModule, DeveloperArtifactAssemblyPickerComponent]
})
export class DeveloperInitStepsComponent implements OnInit {

    hasItinStepCreatePerm = true;
    hasItinStepEditPerm = true;
    hasItinStepDeletePerm = true;
    hasInitStepShowLogPerm = true;

    initSteps: InitStep[] = [];
    artifacts: AssemblyArtifact[] = [];

    constructor(
        private developerService: DeveloperService,
        private initStepService: InitializeService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private modalService: GenericModalService,
        private loadingPanelService: LoadingPanelService,
        private developerArtifactService: DeveloperArtifactService) {

        this.allowUpdatingRow = this.allowUpdatingRow.bind(this);
        this.allowDeletingRow = this.allowDeletingRow.bind(this);
        this.allowShowLog = this.allowShowLog.bind(this);
        this.displayExprArtifact = this.displayExprArtifact.bind(this);
    }

    async ngOnInit() {
        this.artifacts = await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeInitStep]);
        this.hasItinStepCreatePerm = this.currentUserService.hasPermission('Developer_Action_InitStep_Create');
        this.hasItinStepEditPerm = this.currentUserService.hasPermission('Developer_Action_InitStep_Edit');
        this.hasItinStepDeletePerm = this.currentUserService.hasPermission('Developer_Action_InitStep_Delete');
        this.hasInitStepShowLogPerm = this.currentUserService.hasPermission('Developer_Action_ShowLog');
        this.refresh();
    }

    allowUpdatingRow(options: { component: any; row: any; }): boolean {
        const item: InitStep = options?.row?.data;
        if (item) {
            return this.hasItinStepEditPerm;
        }
        return false;
    }

    allowDeletingRow(options: { component: any; row: any; }): boolean {
        const item: InitStep = options?.row?.data;
        if (item) {
            return this.hasItinStepDeletePerm;
        }
        return false;
    }

    allowShowLog(options: { component: any; row: any; }): boolean {
        const item: InitStep = options?.row?.data;
        if (item) {
            return this.hasInitStepShowLogPerm;
        }
        return false;
    }

    displayExprArtifact(e: any) {
        // TODO: Find en bedre måde at håndtere dette, så det ikke er nødvendigt at loade alle artifacts...
        if (this.artifacts) {
            var artifact = this.artifacts.find(a => a.ArtifactId == e.value);
            if (artifact) {
                return `${artifact.Name} [${artifact.ClassName}] [${artifact.Filename}]`;
            }
        }
        return "Unknown...";
    }

    onInitNewRow(e: any): void {
        e.data = { Id: null, Name: '', Description: '' } as InitStep;
    }

    async refresh() {
        this.loadingPanelService.showLoadingPanelDirect('Loading init-steps...');
        this.initSteps = await this.initStepService.getInitSteps();
        this.loadingPanelService.hideLoadingPanel();
    }

    async onRowInserting(e: any): Promise<void> {
        const item = e.data as InitStep;
        const savedStep = await this.saveInitStep(e, item);
        if (savedStep) {
            item.Id = savedStep.Id;
        }
    }

    async onRowUpdated(e: any): Promise<void> {
        var item = e.key as InitStep;
        item = Object.assign({}, item, e.newData); // Clone instance and set updated data.
        await this.saveInitStep(e, item);
    }

    async onRowRemoving(e: any): Promise<void> {
        const item = e.data as InitStep;

        const deferred = $.Deferred();
        e.cancel = deferred.promise();

        try {
            await this.initStepService.deleteInitStep(item.Id);
            this.toastService.showToast('Initialize step deleted...', ToastType.success);
            deferred.resolve(false);
        }
        catch (err) {
            // Process 409 error here; other errors are processed using generic error handling
            if (err?.status === 409) {
                const settings = new ModalSettingsConfirmMultiButton('Delete failed', `Error details: <pre style='white-space: pre-wrap;'>${err.error}</pre>`);
                settings.size = 'lg';
                settings.backdrop = 'static';
                settings.buttons = [new ModalMultiButton('Close', null, 'btn btn-primary')];
                await this.modalService.confirmMultiButton(settings);
            }
            deferred.resolve(true);
        }
    }

    async saveInitStep(e: any, step: InitStep): Promise<InitStep> {
        try {
            // Check if another step is already using the artifact/step-key
            const existing = this.initSteps.find(i => i.StepKey == step.StepKey && i.Id != step.Id);
            if (existing) {
                e.cancel = true;
                var settings = new ModalSettingsMessage();
                settings.size = 'lg';
                settings.backdrop = 'static';
                // Make sure we are on top with this modal!
                settings.windowClass = "ngb-modal-on-top";
                settings.backdropClass = "ngb-modal-on-top-backdrop";
                settings.title = 'Already exist';
                settings.body = 'Another init-step with that StepKey already exists. Name of the existing step: ' + existing.Name;
                this.modalService.message(settings);
                return null;
            }

            const newStep = await this.initStepService.saveInitStep(step);
            this.toastService.showToast('Itinerary step saved...', ToastType.success);
            return newStep;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save itinerary step...', ToastType.error);
            return undefined;
        }
    }

    async setArtifactId(artifactId: string, cellInfo: any) {
        cellInfo.setValue(artifactId || '');
        if (cellInfo.data.ArtifactId != artifactId && artifactId) { // Only check artifact if it has changed!
            try {
                this.loadingPanelService.showLoadingPanelDirect('Verifying artifact...');
                const initStepArtifactInfo = artifactId ? await this.initStepService.getInitStepArtifactInfo(artifactId) : null;

                if (initStepArtifactInfo) {
                    cellInfo.component.cellValue(cellInfo.row.rowIndex, 'StepKey', initStepArtifactInfo.StepKey);
                }
            } finally {
                this.loadingPanelService.hideLoadingPanel();
            }
        }
    }

    onEditorPreparing(e: any) {
        //if (e.dataField === "Description") {
        //    e.editorName = "dxTextArea";
        //}
    }
}
